// Utils
import { formatUrl } from "@/utils/formatters";
import { isUndefined } from "@/utils/checks";
import httpService from "@/services/http";
import { isErrorUnauthorized } from "@/utils/http";
import logout from "@/utils/logout";
import { getApiBaseUrl } from "@/services/api-config";

async function sseErrorHandler(
  error: Event,
  callback?: (error: Event, authError: boolean) => void
): Promise<void> {
  let authError = false;
  try {
    await httpService.getCurrentUserState();
  } catch (e) {
    authError = isErrorUnauthorized(e);
    if (authError) {
      await logout();
    }
  }

  if (callback) {
    callback(error, authError);
  }
}

interface ClientConfig {
  localBaseURL?: string;
  localWithCredentials?: boolean;
}

export interface CustomEventSource {
  on(event: string, callback: (message: { data: any; [key: string]: any }) => void): void;
  catch(callback: (error: Event, authError: boolean) => void): void;
  close(): void;
}

interface SseClient {
  default: {
    baseURL: string;
    withCredentials: boolean;
  };
  connect(url: string, config?: ClientConfig): CustomEventSource;
}

const sseClient: SseClient = {
  default: {
    baseURL: getApiBaseUrl(),
    withCredentials: true,
  },

  connect(
    url: string,
    { localBaseURL, localWithCredentials }: ClientConfig = {}
  ): CustomEventSource {
    const baseURL = localBaseURL ? localBaseURL : this.default.baseURL;
    const fullURL = formatUrl(`${baseURL}${url}`);
    const config = {
      withCredentials: isUndefined(localWithCredentials)
        ? this.default.withCredentials
        : localWithCredentials,
    };

    const eventSource = new EventSource(fullURL, config);

    eventSource.onerror = sseErrorHandler;

    return {
      on(event, callback) {
        eventSource.addEventListener(event, ({ data: dataRaw, ...messageRaw }) => {
          const message = {
            ...messageRaw,
            data: JSON.parse(dataRaw),
          };
          callback(message);
        });
      },

      catch(callback) {
        eventSource.onerror = (error) => sseErrorHandler(error, callback);
      },

      close() {
        eventSource.close();
      },
    };
  },
};

export default sseClient;
